/**
 * Configuracoes SIMULADAS por empresa (RNF19) -- nao e producao.
 *
 * Decisoes nao obvias:
 *  1. Tudo deriva do catalogo (`empresaPorId`, `equipeDaEmpresa`,
 *     `ofertaCiclica`) e de um hash estavel do id: empresa diferente,
 *     horario/instancia/numero/eventos diferentes; a mesma empresa mostra
 *     sempre o mesmo conjunto. Nenhum Math.random.
 *  2. Configuracao NAO e recortada pela janela do cabecalho: nao existe
 *     "horario permitido dos ultimos 30 dias". Por isso o gerador so recebe o
 *     id da empresa -- e a unica tela da previa em que isso e deliberado. Os
 *     numeros que aparecem ("na regua agora", "ultimos 30 dias") sao retratos
 *     com janela propria, escrita no rotulo.
 *  3. A lista de toques ESPELHA a regua padrao do modulo de funis (ids T30m,
 *     T1h, T24h, T48h e D15, D30, D60, D90) sem importar de la: os modulos
 *     nascem em paralelo e um import cruzado quebraria um quando o outro
 *     mudasse. Quando o backend existir, os dois lerao `funnel_touches`.
 *  4. Empresa em implantacao (`onboarding`) nasce com WhatsApp desconectado,
 *     um segredo faltando e SEM evento aceito -- mas COM dois eventos
 *     rejeitados por 401. E o caso real de "colei a URL mas nao mandei o
 *     segredo": a plataforma envia, o motor recusa em silencio e nada entra
 *     na regua. O historico existe para essa falha deixar de ser silenciosa.
 *  5. O webhook GENERICO aparece conectado nas empresas com mais de uma
 *     plataforma (checkout proprio e comum nelas) e desconectado nas demais:
 *     e o que faz a previa mostrar os dois estados do cartao. `Provider` do
 *     catalogo nao conhece `generic`, por isso a regra mora aqui.
 *  6. Historico e fila usam telefones ficticios no padrao do guia
 *     (+55 11 9999x-xxxx) e ofertas do catalogo. Dinheiro em centavos
 *     inteiros, sempre; medias por divisao inteira.
 */

import { menosMinutos } from '../../lib/api';
import { empresaPorId, equipeDaEmpresa, ofertaCiclica, ofertasDaEmpresa } from '../../lib/mock/catalogo';
import type { CompanySummary, FunnelKind, Provider } from '../../types';
import type {
  CadenceSettings,
  CompanySettings,
  FunnelSettings,
  IntegrationsSettings,
  TouchSummary,
  VoiceName,
  WebhookEventLog,
  WebhookEventType,
  WebhookIntegration,
  WebhookProvider,
  Weekday,
} from './types';
import { WEBHOOK_PROVIDER_INFO } from './vocabulario';

export const PROVEDORES: readonly Provider[] = ['kiwify', 'hotmart', 'shopify'];

/** Ordem dos cartoes e da lista `webhooks`: os tres dedicados e, por ultimo, o generico. */
export const PROVEDORES_WEBHOOK: readonly WebhookProvider[] = ['kiwify', 'hotmart', 'shopify', 'generic'];

/** Host publico do motor. Fica aqui (e nao em types) porque e valor, nao contrato. */
export const HOST_WEBHOOK = 'https://app.convertefacil.com.br';

export function urlDoWebhook(provider: WebhookProvider, companyId: string): string {
  return `${HOST_WEBHOOK}/webhooks/${provider}/${encodeURIComponent(companyId)}`;
}

/** Hash pequeno e estavel do id: mesmo id, mesmo numero, em qualquer maquina. */
export function hashSimulado(texto: string): number {
  let h = 7;
  for (let i = 0; i < texto.length; i += 1) {
    h = (h * 31 + texto.charCodeAt(i)) % 10_007;
  }
  return h;
}

/** Celular ficticio no padrao do guia: +55 11 9999x-xxxx (9 digitos apos o DDD). */
export function celularSimulado(semente: number): string {
  return `+55119999${String(semente % 100_000).padStart(5, '0')}`;
}

const MIN = 1;
const HORA = 60;
const DIA = 1440;

/** Toques SEM `waitingNow`: a fila e distribuida por empresa em `montarFunil`. */
const TOQUES_BASE: Readonly<Record<FunnelKind, readonly Omit<TouchSummary, 'waitingNow'>[]>> = {
  carrinho_abandonado: [
    { id: 'T30m', order: 1, delayMinutes: 30 * MIN, channel: 'voice', withEmail: false, enabled: true, isLast: false },
    { id: 'T1h', order: 2, delayMinutes: 1 * HORA, channel: 'whatsapp', withEmail: true, enabled: true, isLast: false },
    { id: 'T24h', order: 3, delayMinutes: 24 * HORA, channel: 'whatsapp', withEmail: true, enabled: true, isLast: false },
    { id: 'T48h', order: 4, delayMinutes: 48 * HORA, channel: 'whatsapp', withEmail: true, enabled: true, isLast: true },
  ],
  expirados: [
    { id: 'D15', order: 1, delayMinutes: 15 * DIA, channel: 'whatsapp', withEmail: true, enabled: true, isLast: false },
    { id: 'D30', order: 2, delayMinutes: 30 * DIA, channel: 'whatsapp', withEmail: true, enabled: true, isLast: false },
    { id: 'D60', order: 3, delayMinutes: 60 * DIA, channel: 'whatsapp', withEmail: true, enabled: true, isLast: false },
    { id: 'D90', order: 4, delayMinutes: 90 * DIA, channel: 'whatsapp', withEmail: true, enabled: true, isLast: true },
  ],
};

/** Mantido para quem ja importava: os toques padrao com fila zero. */
export const TOQUES_RESUMO: Readonly<Record<FunnelKind, readonly TouchSummary[]>> = {
  carrinho_abandonado: TOQUES_BASE.carrinho_abandonado.map((t) => ({ ...t, waitingNow: 0 })),
  expirados: TOQUES_BASE.expirados.map((t) => ({ ...t, waitingNow: 0 })),
};

const FUNIS_TODOS: readonly FunnelKind[] = ['carrinho_abandonado', 'expirados'];
const DIAS_UTEIS: readonly Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri'];
const VOZES: readonly VoiceName[] = ['ana', 'bruno', 'clara'];

/** Mistura realista: carrinho abandonado domina, pagamento aprovado vem em seguida, estorno, chargeback e vencimento sao raros. */
const CICLO_EVENTOS: readonly WebhookEventType[] = [
  'cart_abandoned',
  'cart_abandoned',
  'payment_approved',
  'cart_abandoned',
  'subscription_expired',
  'cart_abandoned',
  'payment_approved',
  'payment_refunded',
  'cart_abandoned',
  'payment_approved',
  'cart_abandoned',
  'payment_chargeback',
];

/** `source` do contrato generico: de onde o POST veio. */
const FONTES_GENERICO: readonly string[] = ['checkout-proprio', 'n8n', 'checkout-proprio', 'make'];

/** Peso de cada toque na fila de hoje: o primeiro concentra mais gente, o ultimo menos. */
const PESOS_DA_FILA: readonly number[] = [45, 30, 15, 10];

/** Empresa fora do catalogo (sessao real sem mock): o minimo para a tela nao quebrar. */
function empresaFallback(companyId: string): CompanySummary {
  return {
    id: companyId,
    name: 'Sua empresa',
    slug: 'sua-empresa',
    segment: '',
    logoInitials: 'SE',
    logoTone: 1,
    logoUrl: null,
    feeBps: 1_500,
    billingModel: 'a_definir',
    status: 'onboarding',
    providers: [],
    funnels: ['carrinho_abandonado'],
    contactName: '',
    contactEmail: '',
    createdAt: menosMinutos(DIA),
  };
}

/** Preco medio das ofertas da empresa, em centavos inteiros (divisao inteira). */
function precoMedioCents(empresa: CompanySummary): number {
  const ofertas = ofertasDaEmpresa(empresa.id);
  if (ofertas.length === 0) {
    return 9_900;
  }
  const soma = ofertas.reduce((acc, o) => acc + o.priceCents, 0);
  return Math.floor(soma / ofertas.length);
}

function estaConectado(empresa: CompanySummary, provider: WebhookProvider, implantando: boolean): boolean {
  if (provider === 'generic') {
    return !implantando && empresa.providers.length >= 2;
  }
  return empresa.providers.includes(provider);
}

function montarEventos(empresa: CompanySummary, provider: WebhookProvider, h: number, i: number, semSegredo: boolean, implantando: boolean): readonly WebhookEventLog[] {
  if (semSegredo) {
    // Dois 401 recentes: a plataforma esta enviando, o motor esta recusando.
    return [0, 1].map((k) => ({
      id: `SIMULADO-evento-${provider}-rej-${k}`,
      receivedAt: menosMinutos(18 + k * 95 + (h % 30)),
      type: null,
      status: 'rejected',
      httpStatus: 401,
      reason: 'assinatura_invalida',
      productName: null,
      amountCents: null,
      phoneE164: null,
      source: null,
      test: false,
    }));
  }
  if (implantando) {
    return [];
  }
  const permitidos = WEBHOOK_PROVIDER_INFO[provider].eventos;
  const total = 7 + ((h + i) % 4);
  const eventos: WebhookEventLog[] = [];
  let minutos = 6 + (h % 41) + i * 17;
  for (let k = 0; k < total; k += 1) {
    const bruto = CICLO_EVENTOS[(h + i * 3 + k) % CICLO_EVENTOS.length] ?? 'cart_abandoned';
    const type: WebhookEventType = permitidos.includes(bruto) ? bruto : 'cart_abandoned';
    const oferta = ofertaCiclica(empresa.id, h + i + k);
    const repetido = k > 0 && (h + k) % 7 === 3;
    const semTelefone = k === 4 && (h + i) % 3 === 0 && type === 'cart_abandoned';
    const source = provider === 'generic' ? (FONTES_GENERICO[(h + k) % FONTES_GENERICO.length] ?? 'checkout-proprio') : null;
    if (semTelefone) {
      eventos.push({
        id: `SIMULADO-evento-${provider}-${k}`,
        receivedAt: menosMinutos(minutos),
        type,
        status: 'rejected',
        httpStatus: 400,
        reason: 'telefone_invalido',
        productName: oferta.name,
        amountCents: oferta.priceCents,
        phoneE164: null,
        source,
        test: false,
      });
    } else {
      eventos.push({
        id: `SIMULADO-evento-${provider}-${k}`,
        receivedAt: menosMinutos(minutos),
        type,
        status: repetido ? 'duplicate' : 'accepted',
        httpStatus: repetido ? 200 : type === 'cart_abandoned' ? 202 : 200,
        reason: null,
        productName: oferta.name,
        amountCents: oferta.priceCents,
        phoneE164: celularSimulado(h * 13 + i * 101 + k * 7),
        source,
        test: false,
      });
    }
    minutos += 37 + ((h + k * 11) % 160);
  }
  return eventos;
}

function montarWebhooks(empresa: CompanySummary, h: number, implantando: boolean): readonly WebhookIntegration[] {
  let primeiroConectado = true;
  return PROVEDORES_WEBHOOK.map((provider, i) => {
    const connected = estaConectado(empresa, provider, implantando);
    // Em implantacao, o primeiro provedor conectado ainda esta sem segredo: e o
    // caso real de "colei a URL mas nao mandei o segredo" que gera 401 silencioso.
    const semSegredo = implantando && connected && primeiroConectado;
    if (connected) {
      primeiroConectado = false;
    }
    const eventos = connected ? montarEventos(empresa, provider, h, i, semSegredo, implantando) : [];
    const ultimoAceito = eventos.find((e) => e.status === 'accepted') ?? null;
    const eventsLast7Days = connected && !implantando ? 28 + ((h + i * 37) % 140) : semSegredo ? 2 : 0;
    const rejectedLast7Days = semSegredo ? eventsLast7Days : eventos.filter((e) => e.status === 'rejected').length;
    return {
      provider,
      connected,
      webhookUrl: urlDoWebhook(provider, empresa.id),
      secretState: connected && !semSegredo ? 'configured' : 'missing',
      lastEventAt: ultimoAceito === null ? null : ultimoAceito.receivedAt,
      lastEventType: ultimoAceito === null ? null : ultimoAceito.type,
      recentEvents: eventos,
      eventsLast7Days,
      rejectedLast7Days,
    };
  });
}

function montarIntegracoes(empresa: CompanySummary, h: number, implantando: boolean): IntegrationsSettings {
  const celular = celularSimulado(h);
  // Fixo ficticio para a origem da ligacao: +55 11 4000-xxxx (8 digitos apos o DDD).
  const fixo = `+55114000${String((h * 7) % 10_000).padStart(4, '0')}`;
  return {
    webhooks: montarWebhooks(empresa, h, implantando),
    whatsapp: {
      instanceName: `cf-${empresa.slug}`,
      phoneE164: celular,
      state: implantando ? 'disconnected' : 'connected',
      connectedSince: implantando ? null : menosMinutos(DIA * (2 + (h % 12)) + (h % 700)),
    },
    voice: {
      assistantName: `Assistente ${empresa.name}`,
      originPhoneE164: fixo,
      voice: VOZES[h % VOZES.length] ?? 'ana',
      state: implantando ? 'pending' : 'ready',
    },
    email: {
      provider: 'a_definir',
      sendingDomain: `envio.${empresa.slug}.exemplo`,
      state: 'awaiting_decision',
    },
  };
}

/** Distribui `total` pessoas pelos toques com pesos fixos, garantindo que a soma bata (o resto vai para o ultimo). */
function distribuirFila(total: number, quantos: number): readonly number[] {
  if (quantos === 0) {
    return [];
  }
  const somaPesos = PESOS_DA_FILA.slice(0, quantos).reduce((a, b) => a + b, 0);
  const partes: number[] = [];
  let acumulado = 0;
  for (let j = 0; j < quantos - 1; j += 1) {
    const parte = Math.floor((total * (PESOS_DA_FILA[j] ?? 0)) / somaPesos);
    partes.push(parte);
    acumulado += parte;
  }
  partes.push(Math.max(0, total - acumulado));
  return partes;
}

function montarFunil(empresa: CompanySummary, kind: FunnelKind, indice: number, h: number, implantando: boolean): FunnelSettings {
  const enabled = empresa.funnels.includes(kind);
  const base = TOQUES_BASE[kind];
  const medio = precoMedioCents(empresa);
  const operando = enabled && !implantando;
  const fila = operando ? 9 + ((h + indice * 7) % 40) : 0;
  const partes = distribuirFila(fila, base.length);
  const recuperados = operando ? 5 + ((h * 3 + indice) % 30) : 0;
  const recuperadosAntes = operando ? 3 + ((h * 5 + indice * 11) % 28) : 0;
  return {
    kind,
    enabled,
    touches: base.map((t, j) => ({ ...t, waitingNow: partes[j] ?? 0 })),
    queuedLeads: fila,
    queuedAmountCents: fila * medio,
    last30Days: { recoveredCount: recuperados, recoveredAmountCents: recuperados * medio },
    previous30Days: { recoveredCount: recuperadosAntes, recoveredAmountCents: recuperadosAntes * medio },
  };
}

function montarRegua(empresa: CompanySummary, h: number, implantando: boolean): CadenceSettings {
  return {
    waitMinutes: 30,
    maxVoiceAttempts: 2,
    contactWindow: {
      start: h % 2 === 0 ? '08:00' : '09:00',
      end: h % 3 === 0 ? '21:00' : '20:00',
      weekdays: h % 2 === 0 ? [...DIAS_UTEIS, 'sat'] : DIAS_UTEIS,
    },
    funnels: FUNIS_TODOS.map((kind, indice) => montarFunil(empresa, kind, indice, h, implantando)),
  };
}

export function simularConfiguracoes(companyId: string): CompanySettings {
  const empresa = empresaPorId(companyId) ?? empresaFallback(companyId);
  const h = hashSimulado(companyId);
  const implantando = empresa.status === 'onboarding';

  return {
    companyId,
    attributionWindowDays: 7,
    // Padrao do produto (ADR-001, E2). As variantes sao de contrato e chegam pela administracao.
    refundPolicy: 'reverte_chargeback',
    brand: {
      name: empresa.name,
      segment: empresa.segment,
      logoUrl: empresa.logoUrl,
      logoTone: empresa.logoTone,
      website: `https://www.${empresa.slug}.exemplo`,
      contactEmail: empresa.contactEmail,
    },
    cadence: montarRegua(empresa, h, implantando),
    integrations: montarIntegracoes(empresa, h, implantando),
    team: equipeDaEmpresa(companyId),
    notifications: {
      dailySummary: true,
      whatsappDisconnected: true,
      chargebackAlert: h % 2 === 0,
      webhookRejected: true,
      weeklyReport: !implantando,
      recipientEmail: empresa.contactEmail,
    },
    updatedAt: menosMinutos(DIA * (1 + (h % 9)) + (h % 600)),
  };
}
